// Activity logging — a plain module (not a hook) so it can be called from
// anywhere, including non-component code like graph.ts's mutation
// functions and the MSAL event callback in msalInstance.ts, neither of
// which can use React hooks. Uses the msalInstance singleton directly to
// acquire the same Entra ID token (audience = our app) the rest of the
// custom API uses, mirroring useAppIdToken's logic without the hook.

import { msalInstance } from "../auth/msalInstance";
import { getFreshIdToken } from "../auth/freshIdToken";

export type ActivityCategory = "Auth" | "Navigation" | "Feature" | "Initiative" | "Platform" | "Library" | "Review";

export interface ActivityEntry {
  timestamp: string;
  category: string;
  event: string;
  entityKind: string;
  entityId: string;
  description: string;
  userName: string;
  userEmail: string;
}

async function getIdToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    return await getFreshIdToken(msalInstance, account);
  } catch {
    return null;
  }
}

/**
 * Fire-and-forget by design — logging must never break the real action it's
 * attached to. Never throws; swallows and warns on failure instead.
 */
export async function logActivity(
  category: ActivityCategory,
  event: string,
  description: string,
  entityKind?: string,
  entityId?: string
): Promise<void> {
  try {
    const idToken = await getIdToken();
    if (!idToken) return;
    await fetch("/api/activity", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Id-Token": idToken },
      body: JSON.stringify({ category, event, entityKind, entityId, description }),
    });
  } catch (err) {
    console.warn("Activity log failed (non-fatal):", err);
  }
}

/** Admin-only — throws on failure, unlike logActivity, since this backs a
 *  real page the caller needs to show an error state for. */
export async function fetchActivity(category?: string): Promise<ActivityEntry[]> {
  const idToken = await getIdToken();
  if (!idToken) throw new Error("Not signed in.");
  const url = category ? `/api/activity?category=${encodeURIComponent(category)}` : "/api/activity";
  const res = await fetch(url, { headers: { "X-Id-Token": idToken } });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(payload?.error ?? `Failed to load activity (${res.status}).`);
  }
  const { entries } = (await res.json()) as { entries: ActivityEntry[] };
  return entries;
}
